import axios from 'axios';
import { Auth0ContextInterface } from '@auth0/auth0-react';
import config from '../../../../config/config';
import { withSilentAccessToken } from '../../../helper';
import { PERMISSIONS } from '../../../constants';
import { EmailTemplatesEnum, THEME_CONFIG_KEYS } from '@lemontaps/lib/constants';
import { HierarchyLevel, NFCCard } from '@lemontaps/lib/types/api';
import { APIResponse } from '@lemontaps/lib/types/global/api';

export const fetchNfcCardConfig = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
) => {
  return await withSilentAccessToken<{ data: APIResponse<NFCCard[]> }>(
    getAccessTokenSilently,
    token =>
      axios.get(config.API_BASE_URL + `theme/nfc-cards`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
  );
};

type ThemeConfigKeyType = THEME_CONFIG_KEYS | EmailTemplatesEnum;

type ThemeConfigQueryParams = {
  key: ThemeConfigKeyType;
  language?: string;
  unitId?: number | null;
  fetchThemeLevel?: boolean;
};

type ThemeConfigResponse<T = unknown> = {
  meta: {
    hierarchyLevel: HierarchyLevel;
    key: ThemeConfigKeyType;
  };
  value: T;
};

export const fetchThemeConfig = async <T = unknown>(
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  payload: ThemeConfigQueryParams | ThemeConfigKeyType,
): Promise<ThemeConfigResponse<T>> => {
  const params = typeof payload === 'string' ? { key: payload } : payload;
  const response = await withSilentAccessToken(
    getAccessTokenSilently,
    token =>
      axios.get(`${config.API_BASE_URL}theme/config`, {
        params: params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    [PERMISSIONS.READ.THEME_GLOBAL],
  );
  if (response.data?.isSuccess) {
    return response.data?.data;
  }
  throw new Error('isSuccess: false');
};

export const fetchPublicThemeConfig = async (
  key: THEME_CONFIG_KEYS,
  payload?: { username?: string; accountId?: string },
) => {
  const response = await axios.get(`${config.API_BASE_URL}theme/public-config`, {
    params: { key: key || '', ...payload },
  });
  if (response.data?.isSuccess) {
    return response.data?.data;
  }
  throw new Error('isSuccess: false');
};

export const writeThemeConfig = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  payload: { key: ThemeConfigKeyType; value: unknown; language?: string; unitId?: number | null },
) => {
  return await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.post(`${config.API_BASE_URL}theme/config`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  );
};

export const deleteThemeConfig = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  payload: {
    key: ThemeConfigKeyType;
    language?: string;
    unitId?: number | null;
    withDeleteUnitConfig?: boolean;
  },
) => {
  return await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.post(`${config.API_BASE_URL}theme/delete-config`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  );
};
